import { useState } from 'react'
import { useAuth } from '@/hooks/useAuth'
import { useUpdateProfile } from '@/hooks/useUpdateProfile'
import { usePhotoUpload } from '@/hooks/usePhotoUpload'
import { supabase } from '@/lib/supabase'
import { pickPhoto, base64ToFile } from '@/lib/camera'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Camera, Loader2, Save, Lock } from 'lucide-react'
import { toast } from '@/lib/toast'
import type { ResidentGender, ResidentCivilStatus } from '@/types/database'

const GENDERS: { value: ResidentGender; label: string }[] = [
  { value: 'male', label: 'Male' }, { value: 'female', label: 'Female' },
  { value: 'other', label: 'Other' }, { value: 'prefer_not_to_say', label: 'Prefer not to say' },
]
const CIVIL_STATUSES: { value: ResidentCivilStatus; label: string }[] = [
  { value: 'single', label: 'Single' }, { value: 'married', label: 'Married' },
  { value: 'widowed', label: 'Widowed' }, { value: 'separated', label: 'Separated' },
  { value: 'divorced', label: 'Divorced' }, { value: 'annulled', label: 'Annulled' },
]
const BLOOD_TYPES = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-', 'Unknown'] as const
const SUFFIXES = ['', 'Jr.', 'Sr.', 'II', 'III', 'IV', 'V'] as const

export function PersonalPage() {
  const { profile } = useAuth()
  const update = useUpdateProfile()
  const photoUpload = usePhotoUpload()
  const isVerified = profile?.verification_status === 'verified'

  const [firstName, setFirstName] = useState(profile?.first_name ?? '')
  const [middleName, setMiddleName] = useState(profile?.middle_name ?? '')
  const [lastName, setLastName] = useState(profile?.last_name ?? '')
  const [suffix, setSuffix] = useState(profile?.suffix ?? '')
  const [nickname, setNickname] = useState(profile?.nickname ?? '')
  const [birthPlace, setBirthPlace] = useState(profile?.birth_place ?? '')
  const [gender, setGender] = useState<ResidentGender>(profile?.gender ?? 'male')
  const [civilStatus, setCivilStatus] = useState<ResidentCivilStatus>(profile?.civil_status ?? 'single')
  const [bloodType, setBloodType] = useState(profile?.blood_type ?? '')
  const [religion, setReligion] = useState(profile?.religion ?? '')
  const [nationality, setNationality] = useState(profile?.nationality ?? 'Filipino')
  const [email, setEmail] = useState(profile?.email ?? '')
  const [mobile, setMobile] = useState(profile?.mobile_number ?? '')
  const [isVoter, setIsVoter] = useState(profile?.is_registered_voter ?? false)
  const [precinct, setPrecinct] = useState(profile?.precinct_number ?? '')
  const [voterId, setVoterId] = useState(profile?.voter_id_number ?? '')

  const [photoUrl, setPhotoUrl] = useState<string | null>(null)
  useState(() => {
    if (profile?.photo_storage_path) {
      supabase.storage.from('resident-photos').createSignedUrl(profile.photo_storage_path, 3600)
        .then(({ data }) => { if (data) setPhotoUrl(data.signedUrl) })
    }
  })

  const handlePhoto = async () => {
    try {
      const result = await pickPhoto()
      if (!result) return
      const file = base64ToFile(result.base64, result.format, 'avatar')
      if (file.size > 5 * 1024 * 1024) { toast.error('Max 5MB'); return }
      const path = await photoUpload.mutateAsync(file)
      const { data } = await supabase.storage.from('resident-photos').createSignedUrl(path, 3600)
      if (data) setPhotoUrl(data.signedUrl)
    } catch (err) {
      if (err instanceof Error && err.message !== 'User cancelled photos app') {
        toast.error(err.message)
      }
    }
  }

  const handleSave = () => {
    const fields: Record<string, unknown> = {
      nickname: nickname || null, birth_place: birthPlace || null,
      civil_status: civilStatus, blood_type: bloodType || null,
      religion: religion || null, nationality: nationality || null,
      email: email || null, mobile_number: mobile || null,
      is_registered_voter: isVoter, precinct_number: isVoter ? precinct || null : null,
      voter_id_number: isVoter ? voterId || null : null,
    }
    if (!isVerified) {
      Object.assign(fields, {
        first_name: firstName, middle_name: middleName || null,
        last_name: lastName, suffix: suffix || null, gender,
      })
    }
    update.mutate(fields)
  }

  if (!profile) return null

  const LockedIcon = () => isVerified ? <Lock className="ml-1 inline h-3 w-3 text-muted-foreground" /> : null
  const lockedTitle = 'Locked — contact your barangay admin to change'

  return (
    <div className="space-y-4">
      {/* Photo */}
      <Card>
        <CardContent className="flex items-center gap-4 pt-6">
          <div className="relative h-20 w-20 shrink-0 overflow-hidden rounded-full bg-muted">
            {photoUrl ? <img src={photoUrl} alt="" className="h-full w-full object-cover" /> : (
              <div className="flex h-full w-full items-center justify-center text-2xl font-bold text-muted-foreground">
                {profile.first_name[0]}{profile.last_name[0]}
              </div>
            )}
          </div>
          <div>
            <p className="font-medium">{profile.first_name} {profile.last_name}</p>
            <p className="text-sm text-muted-foreground">@{profile.username}</p>
            <Button variant="outline" size="sm" className="mt-2" onClick={handlePhoto} disabled={photoUpload.isPending}>
              {photoUpload.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Camera className="h-4 w-4" />}
              Change Photo
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Identity */}
      <Card>
        <CardHeader><CardTitle className="text-base">Identity</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label title={isVerified ? lockedTitle : undefined}>First Name <LockedIcon /></Label>
              <Input value={firstName} onChange={(e) => setFirstName(e.target.value)} disabled={isVerified} />
            </div>
            <div className="space-y-1">
              <Label title={isVerified ? lockedTitle : undefined}>Middle Name <LockedIcon /></Label>
              <Input value={middleName} onChange={(e) => setMiddleName(e.target.value)} disabled={isVerified} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label title={isVerified ? lockedTitle : undefined}>Last Name <LockedIcon /></Label>
              <Input value={lastName} onChange={(e) => setLastName(e.target.value)} disabled={isVerified} />
            </div>
            <div className="space-y-1">
              <Label title={isVerified ? lockedTitle : undefined}>Suffix <LockedIcon /></Label>
              <Select value={suffix} onValueChange={setSuffix} disabled={isVerified}>
                <SelectTrigger><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent>{SUFFIXES.map((s) => <SelectItem key={s || 'none'} value={s || 'none'}>{s || 'None'}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1"><Label>Nickname</Label><Input value={nickname} onChange={(e) => setNickname(e.target.value)} /></div>
            <div className="space-y-1"><Label>Birth Place</Label><Input value={birthPlace} onChange={(e) => setBirthPlace(e.target.value)} /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label title={isVerified ? lockedTitle : undefined}>Gender <LockedIcon /></Label>
              <Select value={gender} onValueChange={(v) => setGender(v as ResidentGender)} disabled={isVerified}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{GENDERS.map((g) => <SelectItem key={g.value} value={g.value}>{g.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Civil Status</Label>
              <Select value={civilStatus} onValueChange={(v) => setCivilStatus(v as ResidentCivilStatus)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{CIVIL_STATUSES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1">
              <Label>Blood Type</Label>
              <Select value={bloodType} onValueChange={setBloodType}>
                <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{BLOOD_TYPES.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1"><Label>Religion</Label><Input value={religion} onChange={(e) => setReligion(e.target.value)} /></div>
            <div className="space-y-1"><Label>Nationality</Label><Input value={nationality} onChange={(e) => setNationality(e.target.value)} /></div>
          </div>
        </CardContent>
      </Card>

      {/* Contact */}
      <Card>
        <CardHeader><CardTitle className="text-base">Contact</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-2 gap-3">
          <div className="space-y-1"><Label>Email</Label><Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} /></div>
          <div className="space-y-1"><Label>Mobile</Label><Input value={mobile} onChange={(e) => setMobile(e.target.value)} placeholder="09XX XXX XXXX" /></div>
        </CardContent>
      </Card>

      {/* Voter */}
      <Card>
        <CardHeader><CardTitle className="text-base">Voter Info</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={isVoter} onChange={(e) => setIsVoter(e.target.checked)} className="h-4 w-4 rounded border-input" />
            Registered voter
          </label>
          {isVoter && (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label>Precinct Number</Label><Input value={precinct} onChange={(e) => setPrecinct(e.target.value)} /></div>
              <div className="space-y-1"><Label>Voter ID Number</Label><Input value={voterId} onChange={(e) => setVoterId(e.target.value)} /></div>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="sticky bottom-20 lg:bottom-4">
        <Button className="w-full" onClick={handleSave} disabled={update.isPending}>
          {update.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          Save Changes
        </Button>
      </div>
    </div>
  )
}
